import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Search, SlidersHorizontal, X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { MenuItem, MenuPopover } from '@/components/ui/Popover';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { Select } from '@/components/ui/Select';
import { TYPE } from '@/lib/typography';
import {
  EMPTY_JOURNAL_HISTORY_FILTERS,
  JOURNAL_ATTACHMENT_FILTERS,
  JOURNAL_DATE_PRESETS,
  JOURNAL_STATUS_FILTERS,
  JOURNAL_TYPE_FILTERS,
  activeJournalFilterKeys,
  clearJournalFilter,
  countActiveJournalFilters,
  type JournalAttachmentFilter,
  type JournalDatePreset,
  type JournalFilterChipKey,
  type JournalHistoryFilterState,
  type JournalStatusFilter,
  type JournalTypeFilter,
} from '@/utils/journalHistoryFilters';

export interface JournalHistoryCourseOption {
  value: string;
  label: string;
}

export interface JournalHistoryPractitionerOption {
  value: string;
  label: string;
}

export interface JournalHistoryFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  datePreset: JournalDatePreset;
  onDatePresetChange: (value: JournalDatePreset) => void;
  customFrom: string;
  customTo: string;
  onCustomRangeChange: (next: { from: string; to: string }) => void;
  type: JournalTypeFilter;
  onTypeChange: (value: JournalTypeFilter) => void;
  status: JournalStatusFilter;
  onStatusChange: (value: JournalStatusFilter) => void;
  attachment: JournalAttachmentFilter;
  onAttachmentChange: (value: JournalAttachmentFilter) => void;
  courseValue?: string;
  onCourseChange?: (value: string) => void;
  courses?: JournalHistoryCourseOption[];
  practitionerValue?: string;
  onPractitionerChange?: (value: string) => void;
  practitioners?: JournalHistoryPractitionerOption[];
}

function toFilterState(props: JournalHistoryFiltersProps): JournalHistoryFilterState {
  return {
    datePreset: props.datePreset,
    customFrom: props.customFrom,
    customTo: props.customTo,
    type: props.type,
    status: props.status,
    attachment: props.attachment,
  };
}

export function JournalHistoryFilters(props: JournalHistoryFiltersProps) {
  const { t } = useTranslation();
  const {
    search,
    onSearchChange,
    datePreset,
    onDatePresetChange,
    customFrom,
    customTo,
    onCustomRangeChange,
    type,
    onTypeChange,
    status,
    onStatusChange,
    attachment,
    onAttachmentChange,
    courseValue = 'all',
    onCourseChange,
    courses = [],
    practitionerValue = '',
    onPractitionerChange,
    practitioners = [],
  } = props;
  const selectedPractitioner = practitioners.find((row) => row.value === practitionerValue);
  const courseOptions = [
    { value: 'all', label: t('journal.all_notes') },
    ...courses.slice(0, 3),
  ];
  const courseOverflow = courses.length > 3;
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const state = toFilterState(props);
  const activeCount = countActiveJournalFilters(state);
  const activeKeys = activeJournalFilterKeys(state);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  const applyState = (next: JournalHistoryFilterState) => {
    onDatePresetChange(next.datePreset);
    onCustomRangeChange({ from: next.customFrom, to: next.customTo });
    onTypeChange(next.type);
    onStatusChange(next.status);
    onAttachmentChange(next.attachment);
  };

  const dateValueLabel = (() => {
    if (datePreset === 'custom' && (customFrom || customTo)) {
      return [customFrom, customTo].filter(Boolean).join(' – ');
    }
    return t(`journal.filter_date_${datePreset}`);
  })();

  const chipLabel = (key: JournalFilterChipKey): string => {
    switch (key) {
      case 'date':
        return t('journal.filters_chip_date', { value: dateValueLabel });
      case 'type':
        return t('journal.filters_chip_type', { value: t('journal.filter_all_types') });
      case 'status':
        return t('journal.filters_chip_status', {
          value: t(`journal.filter_status_${status}`),
        });
      case 'attachment':
        return t('journal.filters_chip_attachments', {
          value: t(`journal.filter_attachments_${attachment}`),
        });
      default: {
        const _exhaustive: never = key;
        return _exhaustive;
      }
    }
  };

  return (
    <div className="space-y-s2">
      <div className="jf-tools">
        <div className="jf-search">
          <Input
            type="search"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={t('journal.search_placeholder')}
            aria-label={t('journal.search_placeholder')}
            leadingIcon={<Search className="h-4 w-4" strokeWidth={1.75} aria-hidden />}
          />
        </div>
        {onCourseChange && !courseOverflow ? (
          <SegmentedControl
            ariaLabel={t('journal.filter_course')}
            value={courseValue}
            onChange={onCourseChange}
            options={courseOptions}
          />
        ) : null}
        {onCourseChange && courseOverflow ? (
          <MenuPopover
            align="start"
            title={t('journal.filter_course')}
            trigger={
              <button type="button" className="jf-sel">
                {courseValue === 'all'
                  ? t('journal.all_notes')
                  : courses.find((row) => row.value === courseValue)?.label ?? t('journal.more_courses')}
                <ChevronDown strokeWidth={1.75} aria-hidden />
              </button>
            }
          >
            <MenuItem active={courseValue === 'all'} onClick={() => onCourseChange('all')}>
              {t('journal.all_notes')}
            </MenuItem>
            {courses.map((course) => (
              <MenuItem
                key={course.value}
                active={courseValue === course.value}
                onClick={() => onCourseChange(course.value)}
              >
                {course.label}
              </MenuItem>
            ))}
          </MenuPopover>
        ) : null}
        {onPractitionerChange ? (
          <MenuPopover
            align="start"
            title={t('journal.filter_practitioner')}
            trigger={
              <button type="button" className="jf-sel">
                {selectedPractitioner
                  ? t('journal.practitioner_named', { name: selectedPractitioner.label })
                  : t('journal.practitioner_all')}
                <ChevronDown strokeWidth={1.75} aria-hidden />
              </button>
            }
          >
            <MenuItem active={!practitionerValue} onClick={() => onPractitionerChange('')}>
              {t('journal.practitioner_all')}
            </MenuItem>
            {practitioners.map((member) => (
              <MenuItem
                key={member.value}
                active={practitionerValue === member.value}
                onClick={() => onPractitionerChange(member.value)}
              >
                {member.label}
              </MenuItem>
            ))}
          </MenuPopover>
        ) : null}
        <div ref={rootRef} className="relative">
        <Button
          type="button"
          variant="secondary"
          data-testid="journal-filters-button"
          aria-expanded={open}
          aria-haspopup="dialog"
          onClick={() => setOpen((value) => !value)}
          className={cn('shrink-0', activeCount > 0 && 'border-ds-teal bg-ds-teal-tint text-ds-teal')}
        >
          <SlidersHorizontal strokeWidth={1.75} aria-hidden />
          {activeCount > 0
            ? t('journal.filters_button_count', { count: activeCount })
            : t('journal.filters_button')}
        </Button>

        {open && (
          <div
            role="dialog"
            aria-label={t('journal.filters_title')}
            data-testid="journal-filters-panel"
            className="absolute right-0 top-full z-30 mt-s1 w-72 max-w-[calc(100vw-2rem)] rounded-pop border border-ds-line bg-ds-card p-s4 shadow-card"
          >
            <div className="mb-s3 flex items-center justify-between">
              <h2 className={cn(TYPE.section, 'text-ds-text')}>
                {t('journal.filters_title')}
              </h2>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setOpen(false)}
                aria-label={t('common.close')}
              >
                <X />
              </Button>
            </div>

            <div className="space-y-s3">
              <Select
                id="journal-filter-date"
                label={t('journal.filter_date')}
                value={datePreset}
                onChange={(event) => onDatePresetChange(event.target.value as JournalDatePreset)}
                aria-label={t('journal.filter_all_dates')}
              >
                {JOURNAL_DATE_PRESETS.map((preset) => (
                  <option key={preset} value={preset}>
                    {t(`journal.filter_date_${preset}`)}
                  </option>
                ))}
              </Select>

              {datePreset === 'custom' && (
                <div className="grid grid-cols-2 gap-s2">
                  <Input
                    id="journal-filter-from"
                    type="date"
                    label={t('journal.filter_from')}
                    value={customFrom}
                    onChange={(event) =>
                      onCustomRangeChange({ from: event.target.value, to: customTo })
                    }
                  />
                  <Input
                    id="journal-filter-to"
                    type="date"
                    label={t('journal.filter_to')}
                    value={customTo}
                    onChange={(event) =>
                      onCustomRangeChange({ from: customFrom, to: event.target.value })
                    }
                  />
                </div>
              )}

              <Select
                id="journal-filter-type"
                label={t('journal.filter_type')}
                value={type}
                onChange={(event) => onTypeChange(event.target.value as JournalTypeFilter)}
                aria-label={t('journal.filter_all_types')}
              >
                {JOURNAL_TYPE_FILTERS.map((value) => (
                  <option key={value} value={value}>
                    {t('journal.filter_all_types')}
                  </option>
                ))}
              </Select>

              <Select
                id="journal-filter-status"
                label={t('journal.filter_status')}
                value={status}
                onChange={(event) => onStatusChange(event.target.value as JournalStatusFilter)}
                aria-label={t('journal.filter_all_status')}
              >
                {JOURNAL_STATUS_FILTERS.map((value) => (
                  <option key={value} value={value}>
                    {value === 'all'
                      ? t('journal.filter_all_status')
                      : t(`journal.filter_status_${value}`)}
                  </option>
                ))}
              </Select>

              <Select
                id="journal-filter-attachments"
                label={t('journal.filter_attachments')}
                value={attachment}
                onChange={(event) =>
                  onAttachmentChange(event.target.value as JournalAttachmentFilter)
                }
                aria-label={t('journal.filter_attachments_all')}
              >
                {JOURNAL_ATTACHMENT_FILTERS.map((value) => (
                  <option key={value} value={value}>
                    {value === 'all'
                      ? t('journal.filter_attachments_all')
                      : t(`journal.filter_attachments_${value}`)}
                  </option>
                ))}
              </Select>
            </div>

            <Button
              type="button"
              variant="ghost"
              onClick={() => applyState(EMPTY_JOURNAL_HISTORY_FILTERS)}
              disabled={activeCount === 0}
              className="mt-s3"
            >
              {t('journal.filters_clear')}
            </Button>
          </div>
        )}
        </div>
      </div>

      {activeKeys.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {activeKeys.map((key) => (
            <span
              key={key}
              className="inline-flex items-center gap-1 rounded-full bg-ds-teal-tint px-s2 py-0.5 text-xs font-medium text-ds-teal"
            >
              {chipLabel(key)}
              <button
                type="button"
                onClick={() => applyState(clearJournalFilter(state, key))}
                aria-label={t('journal.filters_remove_chip', { label: chipLabel(key) })}
                className="rounded-full hover:bg-ds-teal-line"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
